import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { Plus, ChevronDown, ChevronUp, Edit2, Trash2, Save, X, Search } from "lucide-react";
import PaymentModal from "./PaymentModal";
import { formatDate } from "@/lib/dates";
import { formatPassengerName } from "@/lib/passengerName";
import { canEditTab } from "@/lib/permissions";

interface TaxiCallerJob {
  id: string;
  created_at: string | null;
  upload_date: string | null;
  Project: string | null;
  Cost_Code: string | null;
  Account_ID: string | null;
  Reference: string | null;
  Passenger_First_Name: string | null;
  Passenger_Last_Name: string | null;
  Job_ID: string | null;
  Pick_Up_Location: string | null;
  Drop_Off_Location: string | null;
  Stage: string;
  vehicle_number: string | null;
  driver_name: string | null;
  driver_first_name: string | null;
  driver_last_name: string | null;
  trip_total: number | null;
  direct_settled: number | null;
  grand_total: number | null;
  trip_base_fare: number | null;
  job_km: number | null;
  driver_completed: boolean | null;
  fleet: string | null;
  pu_date: string | null;
  pu_time: string | null;
  do_date: string | null;
  do_time: string | null;
  Picked_Up: string | null;
}

interface JobPaymentBreakdown {
  cash: number;
  eftpos: number;
  tm: number;
  tmHoist: number;
  acc: number;
  other: number;
  discount: number;
  eCard: number;
}

const emptyJob: Omit<TaxiCallerJob, 'id' | 'created_at' | 'upload_date'> = {
  Project: "",
  Cost_Code: "",
  Account_ID: "",
  Reference: "",
  Passenger_First_Name: "",
  Passenger_Last_Name: "",
  Job_ID: "",
  Pick_Up_Location: "",
  Drop_Off_Location: "",
  Stage: "Unknown",
  vehicle_number: "",
  driver_name: "",
  driver_first_name: "",
  driver_last_name: "",
  trip_total: null,
  direct_settled: null,
  grand_total: null,
  trip_base_fare: null,
  job_km: null,
  driver_completed: null,
  fleet: "",
  pu_date: "",
  pu_time: "",
  do_date: "",
  do_time: "",
  Picked_Up: "",
};

const emptyBreakdown: JobPaymentBreakdown = {
  cash: 0,
  eftpos: 0,
  tm: 0,
  tmHoist: 0,
  acc: 0,
  other: 0,
  discount: 0,
  eCard: 0,
};

const stageOptions = ["Delivered", "Unknown", "Not started", "Callout", "Waiting", "Passenger on board"];

// Form fields component for adding new jobs - defined outside main component to prevent re-creation on every render
const JobFormFields = ({
  data,
  setData,
  drivers,
  fleetVehicles,
}: {
  data: typeof emptyJob;
  setData: (data: any) => void;
  drivers: any[];
  fleetVehicles: any[];
}) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
    {/* Job Details */}
    <div className="space-y-2">
      <Label className="text-foreground text-sm">Job ID *</Label>
      <Input
        value={data.Job_ID || ""}
        onChange={(e) => setData({ ...data, Job_ID: e.target.value })}
        className="dark-input"
        placeholder="Job ID"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Stage</Label>
      <Select
        value={data.Stage || "Unknown"}
        onValueChange={(value) => setData({ ...data, Stage: value })}
      >
        <SelectTrigger className="dark-input">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {stageOptions.map((stage) => (
            <SelectItem key={stage} value={stage}>
              {stage}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Driver</Label>
      <Select
        value={data.driver_name || "__none__"}
        onValueChange={(value) => {
          const actualValue = value === "__none__" ? null : value;
          const driver = drivers.find((d) => d.driver_name === actualValue);
          setData({
            ...data,
            driver_name: actualValue,
            driver_first_name: driver?.driver_firstname || "",
            driver_last_name: driver?.driver_lastname || "",
          });
        }}
      >
        <SelectTrigger className="dark-input">
          <SelectValue placeholder="Select driver" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">No Driver</SelectItem>
          {drivers.map((driver) => (
            <SelectItem key={driver.driver_name} value={driver.driver_name}>
              {driver.driver_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Vehicle</Label>
      <Select
        value={data.vehicle_number || "__none__"}
        onValueChange={(value) => setData({ ...data, vehicle_number: value === "__none__" ? null : value })}
      >
        <SelectTrigger className="dark-input">
          <SelectValue placeholder="Select vehicle" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none__">No Vehicle</SelectItem>
          {fleetVehicles.map((vehicle) => (
            <SelectItem key={vehicle.fleetNumber} value={vehicle.fleetNumber}>
              {vehicle.fleetNumber}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>


    {/* Passenger Details */}
    <div className="space-y-2">
      <Label className="text-foreground text-sm">Passenger First Name</Label>
      <Input
        value={data.Passenger_First_Name || ""}
        onChange={(e) => setData({ ...data, Passenger_First_Name: e.target.value })}
        className="dark-input"
        placeholder="First name"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Passenger Last Name</Label>
      <Input
        value={data.Passenger_Last_Name || ""}
        onChange={(e) => setData({ ...data, Passenger_Last_Name: e.target.value })}
        className="dark-input"
        placeholder="Last name"
      />
    </div>

    {/* Locations */}
    <div className="space-y-2">
      <Label className="text-foreground text-sm">Pick Up Location</Label>
      <Input
        value={data.Pick_Up_Location || ""}
        onChange={(e) => setData({ ...data, Pick_Up_Location: e.target.value })}
        className="dark-input"
        placeholder="Pick up location"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Drop Off Location</Label>
      <Input
        value={data.Drop_Off_Location || ""}
        onChange={(e) => setData({ ...data, Drop_Off_Location: e.target.value })}
        className="dark-input"
        placeholder="Drop off location"
      />
    </div>

    {/* Date/Time */}
    <div className="space-y-2">
      <Label className="text-foreground text-sm">Pick Up Date</Label>
      <Input
        type="date"
        value={data.pu_date || ""}
        onChange={(e) => setData({ ...data, pu_date: e.target.value })}
        className="dark-input"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Pick Up Time</Label>
      <Input
        type="time"
        value={data.pu_time || ""}
        onChange={(e) => setData({ ...data, pu_time: e.target.value })}
        className="dark-input"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Drop Off Date</Label>
      <Input
        type="date"
        value={data.do_date || ""}
        onChange={(e) => setData({ ...data, do_date: e.target.value })}
        className="dark-input"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Drop Off Time</Label>
      <Input
        type="time"
        value={data.do_time || ""}
        onChange={(e) => setData({ ...data, do_time: e.target.value })}
        className="dark-input"
      />
    </div>

    {/* Financial */}
    <div className="space-y-2">
      <Label className="text-foreground text-sm">Trip Total ($)</Label>
      <Input
        type="number"
        step="0.01"
        value={data.trip_total ?? ""}
        onChange={(e) => setData({ ...data, trip_total: e.target.value ? parseFloat(e.target.value) : null })}
        className="dark-input"
        placeholder="0.00"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Direct Settled ($)</Label>
      <Input
        type="number"
        step="0.01"
        value={data.direct_settled ?? ""}
        onChange={(e) => setData({ ...data, direct_settled: e.target.value ? parseFloat(e.target.value) : null })}
        className="dark-input"
        placeholder="0.00"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Grand Total ($)</Label>
      <Input
        type="number"
        step="0.01"
        value={data.grand_total ?? ""}
        onChange={(e) => setData({ ...data, grand_total: e.target.value ? parseFloat(e.target.value) : null })}
        className="dark-input"
        placeholder="0.00"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Trip Base Fare ($)</Label>
      <Input
        type="number"
        step="0.01"
        value={data.trip_base_fare ?? ""}
        onChange={(e) => setData({ ...data, trip_base_fare: e.target.value ? parseFloat(e.target.value) : null })}
        className="dark-input"
        placeholder="0.00"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Job KM</Label>
      <Input
        type="number"
        step="0.001"
        value={data.job_km ?? ""}
        onChange={(e) => setData({ ...data, job_km: e.target.value ? parseFloat(e.target.value) : null })}
        className="dark-input"
        placeholder="0.000"
      />
    </div>

    {/* Account/Project Details */}
    <div className="space-y-2">
      <Label className="text-foreground text-sm">Project</Label>
      <Input
        value={data.Project || ""}
        onChange={(e) => setData({ ...data, Project: e.target.value })}
        className="dark-input"
        placeholder="Project"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Cost Code</Label>
      <Input
        value={data.Cost_Code || ""}
        onChange={(e) => setData({ ...data, Cost_Code: e.target.value })}
        className="dark-input"
        placeholder="Cost code"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Account ID</Label>
      <Input
        value={data.Account_ID || ""}
        onChange={(e) => setData({ ...data, Account_ID: e.target.value })}
        className="dark-input"
        placeholder="Account ID"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Reference</Label>
      <Input
        value={data.Reference || ""}
        onChange={(e) => setData({ ...data, Reference: e.target.value })}
        className="dark-input"
        placeholder="Reference"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Fleet</Label>
      <Input
        value={data.fleet || ""}
        onChange={(e) => setData({ ...data, fleet: e.target.value })}
        className="dark-input"
        placeholder="Fleet"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Picked Up</Label>
      <Input
        value={data.Picked_Up || ""}
        onChange={(e) => setData({ ...data, Picked_Up: e.target.value })}
        className="dark-input"
        placeholder="Picked up status"
      />
    </div>

    {/* Driver Details (manual override) */}
    <div className="space-y-2">
      <Label className="text-foreground text-sm">Driver First Name</Label>
      <Input
        value={data.driver_first_name || ""}
        onChange={(e) => setData({ ...data, driver_first_name: e.target.value })}
        className="dark-input"
        placeholder="Driver first name"
      />
    </div>

    <div className="space-y-2">
      <Label className="text-foreground text-sm">Driver Last Name</Label>
      <Input
        value={data.driver_last_name || ""}
        onChange={(e) => setData({ ...data, driver_last_name: e.target.value })}
        className="dark-input"
        placeholder="Driver last name"
      />
    </div>
  </div>
);


const JobInput: React.FC = () => {
  const { drivers, fleetVehicles, effectivePermissions } = useAppContext();
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) only ever sees/enters jobs for their linked driver(s), and
  // can't touch this tab at all without edit rights (it's pure data
  // entry -- there's no meaningful "view only" mode for it). Unrestricted
  // (the default -- everyone today) is unaffected.
  const canEdit = canEditTab(effectivePermissions, "jobinput");
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;
  const scopedDrivers = scopedDriverNames ? drivers.filter((d) => scopedDriverNames.includes(d.driver_name)) : drivers;
  const scopedFleetVehicles = effectivePermissions.isUnrestricted
    ? fleetVehicles
    : fleetVehicles.filter((v) => effectivePermissions.linkedVehicleNumbers.includes(v.fleetNumber));
  const [jobs, setJobs] = useState<TaxiCallerJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [newJob, setNewJob] = useState(emptyJob);
  const [searchTerm, setSearchTerm] = useState("");
  const [saving, setSaving] = useState(false);

  // Filter state
  const [filterDriver, setFilterDriver] = useState<string>("__all__");
  const [filterDate, setFilterDate] = useState<string>("");

  // Payment modal state
  const [selectedJob, setSelectedJob] = useState<TaxiCallerJob | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [jobBreakdown, setJobBreakdown] = useState<JobPaymentBreakdown>(emptyBreakdown);
  const [driverNote, setDriverNote] = useState("");
  const [isSavingPayment, setIsSavingPayment] = useState(false);

  // Fetch jobs from database - only where driver_completed is NULL
  const fetchJobs = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("taxicaller_data")
        .select("*")
        .is("driver_completed", null)
        .order("created_at", { ascending: false })
        .limit(100);
      if (scopedDriverNames) query = query.in("driver_name", scopedDriverNames.length > 0 ? scopedDriverNames : ["__none__"]);
      const { data, error } = await query;

      if (error) throw error;
      setJobs(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load jobs: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add new job
  const handleAddJob = async () => {
    if (!canEdit) return;
    if (!newJob.Job_ID) {
      toast({
        title: "Error",
        description: "Job ID is required",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const jobData = {
        ...newJob,
        trip_total: newJob.trip_total || null,
        direct_settled: newJob.direct_settled || null,
        grand_total: newJob.grand_total || null,
        trip_base_fare: newJob.trip_base_fare || null,
        job_km: newJob.job_km || null,
        vehicle_number: newJob.vehicle_number || null,
        driver_name: newJob.driver_name || null,
        pu_date: newJob.pu_date || null,
        pu_time: newJob.pu_time || null,
        do_date: newJob.do_date || null,
        do_time: newJob.do_time || null,
        driver_completed: null, // Ensure new jobs have driver_completed as null
      };

      const { error } = await supabase.from("taxicaller_data").insert([jobData]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Job added successfully",
      });

      setNewJob(emptyJob);
      setIsAddOpen(false);
      fetchJobs();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to add job: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Delete job
  const handleDeleteJob = async (id: string) => {
    if (!canEdit) return;
    if (!confirm("Are you sure you want to delete this job?")) return;

    try {
      const { error } = await supabase.from("taxicaller_data").delete().eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Job deleted successfully",
      });

      fetchJobs();
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to delete job: ${error.message}`,
        variant: "destructive",
      });
    }
  };

  // Open payment modal for a job
  const openPaymentModal = (job: TaxiCallerJob) => {
    setSelectedJob(job);
    setJobBreakdown({ ...emptyBreakdown });
    setDriverNote("");
    setIsModalOpen(true);
  };

  // Handle breakdown change from modal
  const handleBreakdownChange = (field: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setJobBreakdown((prev) => ({
      ...prev,
      [field]: numValue,
    }));
  };

  // Save payment and update driver_completed
  const handleSavePayment = async () => {
    if (!selectedJob || !canEdit) return;

    setIsSavingPayment(true);
    try {
      const paidAmount =
        jobBreakdown.cash +
        jobBreakdown.eftpos +
        jobBreakdown.tm +
        jobBreakdown.tmHoist +
        jobBreakdown.acc +
        jobBreakdown.other +
        jobBreakdown.discount +
        jobBreakdown.eCard;

      const insertData = {
        taxicaller_job_id: selectedJob.Job_ID,
        driver_name: selectedJob.driver_name,
        vehicle_number: selectedJob.vehicle_number,
        cash_amt: jobBreakdown.cash,
        eftpos_amt: jobBreakdown.eftpos,
        totalmobility_amt: jobBreakdown.tm,
        tm_hoist_amt: jobBreakdown.tmHoist,
        account_amt: jobBreakdown.acc,
        other_amt: jobBreakdown.other,
        discount: jobBreakdown.discount,
        ecard_amt: jobBreakdown.eCard,
        paid_amount: paidAmount,
        pu_date: selectedJob.pu_date,
        pu_time: selectedJob.pu_time,
        do_time: selectedJob.do_time,
        driver_notes: driverNote || null,
      };

      // Insert into driver_completed_payments
      const { error: insertError } = await supabase
        .from("driver_completed_payments")
        .insert([insertData]);

      if (insertError) throw insertError;

      // Update driver_completed to true in taxicaller_data
      const { error: updateError } = await supabase
        .from("taxicaller_data")
        .update({ driver_completed: true })
        .eq("Job_ID", selectedJob.Job_ID);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Payment saved successfully",
      });

      // Remove job from list and close modal
      setJobs((prev) => prev.filter((j) => j.Job_ID !== selectedJob.Job_ID));
      setIsModalOpen(false);
      setSelectedJob(null);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to save payment: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsSavingPayment(false);
    }
  };

  // Get unique drivers from jobs for filter dropdown
  const uniqueDrivers = Array.from(new Set(jobs.map(j => j.driver_name).filter(Boolean))).sort();

  // Filter jobs based on search, driver, and date
  const filteredJobs = jobs.filter((job) => {
    // Search filter
    const search = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || (
      (job.Job_ID?.toLowerCase().includes(search)) ||
      (job.driver_name?.toLowerCase().includes(search)) ||
      (job.Passenger_First_Name?.toLowerCase().includes(search)) ||
      (job.Passenger_Last_Name?.toLowerCase().includes(search)) ||
      (job.Pick_Up_Location?.toLowerCase().includes(search)) ||
      (job.Drop_Off_Location?.toLowerCase().includes(search))
    );

    // Driver filter
    const matchesDriver = filterDriver === "__all__" || job.driver_name === filterDriver;

    // Date filter
    const matchesDate = !filterDate || job.pu_date === filterDate;

    return matchesSearch && matchesDriver && matchesDate;
  });

  return (
    <Card className="shadow-lg bg-card border border-border">
      <CardHeader>
        <CardTitle className="text-xl text-primary">Job Input</CardTitle>
        <p className="text-sm text-muted-foreground">
          Enter missed bookings into the TaxiCaller data table. Showing jobs where driver has not completed payment entry.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Add New Job Collapsible */}
        <Collapsible open={isAddOpen} onOpenChange={setIsAddOpen}>
          <CollapsibleTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-between border-yellow-600 text-yellow-400 hover:bg-yellow-900"
            >
              <span className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add New Job
              </span>
              {isAddOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="border border-border rounded-lg mt-2 bg-card/50">
              <JobFormFields data={newJob} setData={setNewJob} drivers={scopedDrivers} fleetVehicles={scopedFleetVehicles} />
              <div className="flex gap-2 p-4 pt-0">
                <Button
                  onClick={handleAddJob}
                  disabled={saving}
                  className="golden-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : "Save Job"}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setNewJob(emptyJob);
                    setIsAddOpen(false);
                  }}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>


        {/* Filters Row */}
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <Label className="text-foreground text-sm mb-1 block">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by Job ID, passenger, or location..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="dark-input pl-10"
              />
            </div>
          </div>

          {/* Driver Filter */}
          <div className="w-[200px]">
            <Label className="text-foreground text-sm mb-1 block">Driver</Label>
            <Select
              value={filterDriver}
              onValueChange={setFilterDriver}
            >
              <SelectTrigger className="dark-input">
                <SelectValue placeholder="All Drivers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Drivers</SelectItem>
                {uniqueDrivers.map((driver) => (
                  <SelectItem key={driver} value={driver as string}>
                    {driver}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Filter */}
          <div className="w-[180px]">
            <Label className="text-foreground text-sm mb-1 block">Date</Label>
            <Input
              type="date"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
              className="dark-input"
            />
          </div>

          {/* Clear Filters Button */}
          {(filterDriver !== "__all__" || filterDate) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFilterDriver("__all__");
                setFilterDate("");
              }}
              className="h-10"
            >
              <X className="w-4 h-4 mr-1" />
              Clear Filters
            </Button>
          )}
        </div>


        {/* Jobs Table */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-medium text-foreground">Job ID</th>
                  <th className="text-left p-3 font-medium text-foreground">Driver</th>
                  <th className="text-left p-3 font-medium text-foreground">Vehicle</th>
                  <th className="text-left p-3 font-medium text-foreground">Passenger</th>
                  <th className="text-left p-3 font-medium text-foreground">Pick Up</th>
                  <th className="text-left p-3 font-medium text-foreground">Drop Off</th>
                  <th className="text-left p-3 font-medium text-foreground">Date</th>
                  <th className="text-left p-3 font-medium text-foreground">Total</th>
                  <th className="text-left p-3 font-medium text-foreground">Stage</th>
                  <th className="text-left p-3 font-medium text-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      Loading jobs...
                    </td>
                  </tr>
                ) : filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="p-8 text-center text-muted-foreground">
                      {searchTerm ? "No jobs match your search" : "No incomplete jobs found"}
                    </td>
                  </tr>
                ) : (
                  filteredJobs.map((job) => (
                    <tr key={job.id} className="border-t border-border hover:bg-muted/30 cursor-pointer" onClick={() => openPaymentModal(job)}>
                      <td className="p-3 text-foreground font-mono">{job.Job_ID || "-"}</td>
                      <td className="p-3 text-foreground">{job.driver_name || "-"}</td>
                      <td className="p-3 text-foreground">{job.vehicle_number || "-"}</td>
                      <td className="p-3 text-foreground">
                        {job.Passenger_First_Name || job.Passenger_Last_Name
                          ? formatPassengerName(job.Passenger_First_Name, job.Passenger_Last_Name)
                          : "-"}
                      </td>
                      <td className="p-3 text-foreground max-w-[150px] truncate" title={job.Pick_Up_Location || ""}>
                        {job.Pick_Up_Location || "-"}
                      </td>
                      <td className="p-3 text-foreground max-w-[150px] truncate" title={job.Drop_Off_Location || ""}>
                        {job.Drop_Off_Location || "-"}
                      </td>
                      <td className="p-3 text-foreground">{formatDate(job.pu_date)}</td>
                      <td className="p-3 text-foreground">
                        {job.trip_total != null ? `$${job.trip_total.toFixed(2)}` : "-"}
                      </td>
                      <td className="p-3">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            job.Stage === "Delivered"
                              ? "bg-green-900/50 text-green-400"
                              : job.Stage === "Passenger on board"
                              ? "bg-blue-900/50 text-blue-400"
                              : job.Stage === "Callout"
                              ? "bg-yellow-900/50 text-yellow-400"
                              : job.Stage === "Waiting"
                              ? "bg-orange-900/50 text-orange-400"
                              : "bg-gray-700/50 text-gray-400"
                          }`}
                        >
                          {job.Stage}
                        </span>
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>

                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openPaymentModal(job)}
                            className="h-8 w-8 p-0"
                            title="Enter Payment"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteJob(job.id)}
                            className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                            title="Delete Job"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Job count */}
        <p className="text-sm text-muted-foreground">
          Showing {filteredJobs.length} of {jobs.length} incomplete jobs
        </p>
      </CardContent>

      {/* Payment Modal */}
      {selectedJob && (
        <PaymentModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedJob(null);
          }}
          job={selectedJob}
          breakdown={jobBreakdown}
          onBreakdownChange={handleBreakdownChange}
          onSave={handleSavePayment}
          isSaving={isSavingPayment}
          driverNote={driverNote}
          onDriverNoteChange={setDriverNote}
        />
      )}
    </Card>
  );
};

export default JobInput;
